import { QueryClient } from "@tanstack/react-query";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
  },
});

// ── Query key factories ──

export const userKeys = {
  all: ["users"] as const,
  list: (filters?: { search?: string }) => ["users", "list", filters] as const,
} as const;

export const projectKeys = {
  all: ["projects"] as const,
  detail: (id: string) => ["projects", id] as const,
};
export const rfiKeys = { all: ["rfis"] as const };
export const submittalKeys = { all: ["submittals"] as const };
export const dailyLogKeys = {
  all: ["daily-logs"] as const,
  detail: (id: string) => ["daily-logs", id] as const,
};
export const punchListKeys = { all: ["punch-list"] as const };
export const inspectionKeys = { all: ["inspections"] as const };
export const companyKeys = {
  all: ["companies"] as const,
  detail: (id: string) => ["companies", id] as const,
};
export const contactKeys = {
  all: ["contacts"] as const,
  detail: (id: string) => ["contacts", id] as const,
  byCompany: (companyId: string) => ["contacts", "company", companyId] as const,
  projectContacts: (projectId: string) => ["project-contacts", projectId] as const,
};
export const commitmentKeys = {
  all: ["commitments"] as const,
  detail: (id: string) => ["commitments", id] as const,
  changeOrders: (id: string) => ["commitments", id, "change-orders"] as const,
};
export const changeOrderKeys = {
  all: ["change-orders"] as const,
  project: (projectId: string, statuses?: readonly string[]) =>
    ["change-orders", "project", projectId, statuses ?? null] as const,
  detail: (id: string) => ["change-orders", id] as const,
  forCommitment: (parentId: string) => ["change-orders", "commitment", parentId] as const,
};
export const budgetKeys = {
  project: (projectId: string) => ["budget", projectId] as const,
  costCodes: (projectId: string) => ["cost-codes", projectId] as const,
};
export const buyLogKeys = {
  project: (projectId: string) => ["buy-log", projectId] as const,
};
export const costCodeKeys = {
  all: ["cost-codes"] as const,
};
export const payAppKeys = {
  sub: ["pay-apps", "sub"] as const,
  subDetail: (id: string) => ["pay-apps", "sub", id] as const,
  owner: ["pay-apps", "owner"] as const,
  ownerDetail: (id: string) => ["pay-apps", "owner", id] as const,
  ownerSubBreakdown: (id: string) => ["pay-apps", "owner", id, "sub-breakdown"] as const,
  ownerSubBreakdownCostCode: (id: string, costCodeId: string) =>
    ["pay-apps", "owner", id, "sub-breakdown", costCodeId] as const,
  ownerGcDirectHistory: (id: string, lineItemId?: string) =>
    ["pay-apps", "owner", id, "gc-direct-history", lineItemId ?? "all"] as const,
  ownerRefreshPreview: (id: string) => ["pay-apps", "owner", id, "refresh-preview"] as const,
  ownerGrRefreshPreview: (id: string) => ["pay-apps", "owner", id, "gr-refresh-preview"] as const,
  billingPeriods: ["billing-periods"] as const,
};
export const complianceKeys = {
  all: ["compliance"] as const,
  list: (companyId: string | null) => ["compliance", "list", companyId] as const,
  profile: (companyId: string) => ["compliance", "profile", companyId] as const,
  history: (companyId: string, documentTypeId: string) =>
    ["compliance", "history", companyId, documentTypeId] as const,
};
export const milestoneKeys = {
  all: ["milestones", "all"] as const,
  project: (projectId: string) => ["milestones", projectId] as const,
  history: (milestoneId: string) => ["milestones", "history", milestoneId] as const,
  templates: ["milestone-templates"] as const,
};
export const taskKeys = {
  my: () => ["tasks", "my"] as const,
  project: (projectId: string) => ["tasks", projectId] as const,
  files: (projectId: string, taskId: string) => ["tasks", projectId, taskId, "files"] as const,
  comments: (projectId: string, taskId: string) =>
    ["tasks", projectId, taskId, "comments"] as const,
  activity: (projectId: string, taskId: string) =>
    ["tasks", projectId, taskId, "activity"] as const,
  dependencies: (projectId: string, taskId: string) =>
    ["tasks", projectId, taskId, "dependencies"] as const,
  checklist: (projectId: string, taskId: string) =>
    ["tasks", projectId, taskId, "checklist"] as const,
  attachments: (projectId: string, taskId: string) =>
    ["tasks", projectId, taskId, "attachments"] as const,
  blastRadius: (projectId: string, taskId: string, fileId: string) =>
    ["tasks", projectId, taskId, "attachments", "blast-radius", fileId] as const,
};
export const taskTemplateKeys = {
  all: ["task-templates"] as const,
  detail: (id: string) => ["task-templates", id] as const,
};
export const fileKeys = {
  all: ["files"] as const,
  folder: (projectId: string, folderId: string) =>
    ["files", "folder", projectId, folderId] as const,
  unfiled: (projectId: string) => ["files", "unfiled", projectId] as const,
  linkedTasks: (fileId: string) => ["files", fileId, "linked-tasks"] as const,
  accessHistory: (fileId: string, filters?: Record<string, string | undefined>) =>
    ["files", fileId, "access-history", filters ?? {}] as const,
};
export const taskDocumentKeys = {
  project: (projectId: string, filters?: Record<string, unknown>) =>
    ["task-documents", projectId, filters ?? null] as const,
  count: (projectId: string) => ["task-documents", projectId, "count"] as const,
};
export const folderKeys = {
  templates: ["folder-templates"] as const,
  templatePaths: (templateId: string) => ["folder-template-paths", templateId] as const,
  project: (projectId: string) => ["project-folders", projectId] as const,
};
export const projectTeamRoleKeys = {
  all: ["project-team-roles"] as const,
};
export const projectTeamKeys = {
  project: (projectId: string) => ["project-team", projectId] as const,
};
export const projectPulseKeys = {
  project: (projectId: string) => ["project-pulse", projectId] as const,
};
export const projectAttentionKeys = {
  project: (projectId: string) => ["project-attention", projectId] as const,
};
export const projectOpenItemsKeys = {
  project: (projectId: string) => ["project-open-items", projectId] as const,
};
export const projectActivityKeys = {
  project: (projectId: string) => ["project-activity", projectId] as const,
};
export const projectContractorKeys = {
  project: (projectId: string) => ["project-contractors", projectId] as const,
  tasks: (projectId: string, contractorId: string) =>
    ["project-contractors", projectId, contractorId, "tasks"] as const,
  commitments: (projectId: string, contractorId: string) =>
    ["project-contractors", projectId, contractorId, "commitments"] as const,
};
export const jobCostKeys = { all: ["job-costs"] as const };
export const notificationKeys = { all: ["notifications"] as const };
export const conversationKeys = {
  detail: (id: string) => ["conversations", id] as const,
};
export const auditKeys = {
  trail: (filters?: Record<string, unknown>) => ["audit", "trail", filters ?? null] as const,
};
export const onboardingKeys = {
  status: () => ["onboarding", "status"] as const,
  notificationDefaults: () => ["onboarding", "notification-defaults"] as const,
};
export const versionKeys = { all: ["version"] as const };

export const grInvoiceKeys = {
  all: ["gr-invoices"] as const,
  list: (filters?: Record<string, unknown>) => ["gr-invoices", "list", filters ?? null] as const,
  detail: (id: string) => ["gr-invoices", id] as const,
  pendingVista: (filters?: Record<string, unknown>) =>
    ["gr-invoices", "pending-vista", filters ?? null] as const,
};

export const grSovKeys = {
  list: (projectId: string) => ["gr-sov", projectId] as const,
  variance: (projectId: string) => ["gr-variance", projectId] as const,
};

export const gcLineItemKeys = {
  all: ["gc-line-items"] as const,
  project: (projectId: string) => ["gc-line-items", "project", projectId] as const,
  tracker: (projectId: string) => ["gc-line-items", "tracker", projectId] as const,
};

export const gcLineItemTemplateKeys = {
  all: ["gc-line-item-templates"] as const,
  list: () => ["gc-line-item-templates", "list"] as const,
  detail: (id: string) => ["gc-line-item-templates", "detail", id] as const,
};

export const orgFileKeys = {
  all: ["org-files"] as const,
  list: (filters?: Record<string, unknown>) => ["org-files", "list", filters ?? null] as const,
  folders: () => ["org-files", "folders"] as const,
  folderDrilldown: (folderPath: string, filters?: Record<string, unknown>) =>
    ["org-files", "folder-drilldown", folderPath, filters ?? null] as const,
};
